import { AppointmentField } from './AppointmentField';
import { getFullAddress } from '@/utils';
import classNames from 'classnames';
import { GlobalContext } from '@/context';
import { useContext, useEffect, useState } from 'react';
import mmjCardService from '@/services/mmjCard.service';

const DOC_STATES = ['va', 'ga', 'pa', 'md'];
const FILE_STATES = ['va', 'ga'];
const TEXT_STATES = ['pa', 'md'];

function PatientDocuments({ patientId, patientState }) {
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [signedUrls, setSignedUrls] = useState([]);

  const state = patientState ? patientState.toLowerCase() : '';

  useEffect(() => {
    if (DOC_STATES.includes(state)) {
      loadCard();
    } else {
      setLoading(false);
    }
  }, [patientId]);

  const loadCard = async () => {
    try {
      const {
        data: { data: cards },
      } = await mmjCardService.getCards(patientId);
      if (cards && cards.length > 0) {
        const stateCards = cards
          .filter((c) => c.state && c.state.toLowerCase() === state)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const found = stateCards[0] || cards[0];
        setCard(found);

        if (FILE_STATES.includes(state) && found) {
          let rawUrls = found.patient_document_urls || [];
          if (typeof rawUrls === 'string') {
            try {
              rawUrls = JSON.parse(rawUrls);
            } catch (e) {
              rawUrls = [];
            }
          }
          if (Array.isArray(rawUrls) && rawUrls.length > 0) {
            try {
              const { data: signed } = await mmjCardService.getSignedViewUrls(
                rawUrls,
              );
              setSignedUrls(signed);
            } catch (err) {
              console.log('Failed to get signed URLs:', err);
              setSignedUrls([]);
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  if (!DOC_STATES.includes(state)) return null;
  if (loading)
    return <AppointmentField title="Documents">Loading...</AppointmentField>;

  if (FILE_STATES.includes(state)) {
    const handleDownload = async (url, filename) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        window.open(url, '_blank');
      }
    };

    return (
      <AppointmentField title="Patient ID Documents">
        {signedUrls.length > 0 ? (
          <div className="space-y-3">
            {signedUrls.map((item, i) => {
              const viewUrl = item.signed_url || item.original;
              const filename =
                item.original.split('/').pop().split('?')[0] ||
                `patient_id_${i + 1}.jpg`;
              return (
                <div key={i} className="flex items-center space-x-3">
                  <a href={viewUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={viewUrl}
                      alt={`ID ${i + 1}`}
                      className="h-16 w-auto rounded border border-gray-200 hover:border-mmj-blue"
                    />
                  </a>
                  <div className="flex flex-col">
                    <a
                      href={viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-mmj-blue hover:underline"
                    >
                      View full size
                    </a>
                    <button
                      type="button"
                      className="text-sm text-mmj-blue hover:underline text-left"
                      onClick={() => handleDownload(viewUrl, filename)}
                    >
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-gray-400">
            Patient has not uploaded their ID yet.
          </span>
        )}
      </AppointmentField>
    );
  }

  if (TEXT_STATES.includes(state)) {
    const label = state === 'md' ? 'MMCC Number' : 'PA Patient ID';
    const value = card && card.state_card_id;
    return (
      <AppointmentField title={label}>
        {value ? (
          <span className="font-medium">{value}</span>
        ) : (
          <span className="text-gray-400">
            Patient has not entered their ID number yet.
          </span>
        )}
      </AppointmentField>
    );
  }

  return null;
}

export function AppointmentDetails(props) {
  const { appointment, isUpcoming, onRecommendation, onPaperwork, review } =
    props;
  const { allStates } = useContext(GlobalContext);
  const fullAddress = getFullAddress(appointment.patient, allStates);
  const safeReview = review || { stars: null, comments: null };
  return (
    <>
      <AppointmentField title="Full address">{fullAddress}</AppointmentField>
      <AppointmentField title="Phone number">
        {appointment.patient.phone}
      </AppointmentField>
      <AppointmentField title="Email address">
        {appointment.patient.email}
      </AppointmentField>
      <AppointmentField title="MMJ Card type">
        {appointment.card_type === 'new-card' ? 'New' : 'Renewal'}
      </AppointmentField>
      {isUpcoming ? (
        <AppointmentField title="Patient Phone">
          <a
            href={`tel:${
              appointment.patient.phone_number || appointment.patient.phone
            }`}
            className="text-mmj-blue font-medium hover:underline"
          >
            {appointment.patient.phone_number ||
              appointment.patient.phone ||
              'N/A'}
          </a>
        </AppointmentField>
      ) : (
        <AppointmentField title="Review">
          {safeReview.stars
            ? `${safeReview.stars} ${
                safeReview.stars > 1 ? 'stars' : 'star'
              }: ${safeReview.comments}`
            : 'n/a'}
        </AppointmentField>
      )}
      <PatientDocuments
        patientId={appointment.patient_id || appointment.patient.uid}
        patientState={appointment.patient.state}
      />
      <AppointmentField title="Recommended?">
        <div className="flex items-center">
          <button
            type="button"
            className={classNames('button mr-2', {
              'button-primary': appointment.provider_recommendation === true,
              'button-secondary': appointment.provider_recommendation !== true,
            })}
            onClick={() => {
              onRecommendation(true);
            }}
          >
            Yes
          </button>
          <button
            type="button"
            className={classNames('button', {
              'button-primary': appointment.provider_recommendation === false,
              'button-secondary': appointment.provider_recommendation !== false,
            })}
            onClick={() => {
              onRecommendation(false);
            }}
          >
            No
          </button>
        </div>
      </AppointmentField>
      <AppointmentField isEndItem title="Paperwork?">
        {appointment.is_paperwork_completed ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              onPaperwork(false);
            }}
          >
            Completed
          </button>
        ) : (
          <button
            type="button"
            className="button button-secondary flex items-center"
            onClick={() => {
              onPaperwork(true);
            }}
          >
            Not Completed
          </button>
        )}
      </AppointmentField>
    </>
  );
}
